import { useState } from 'react';
import {
  Alert, Button, Form, Input, InputNumber, Modal, Popconfirm, Select, Space, Switch, Table, Tag, Typography, message,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { metadataApi } from '@api/metadataApi';
import { acqListReferenceTypes } from '@components/forms/referenceTypes';
import type {
  MetaDocumentTypeDto, MetaAttributeDto,
  CreateMetaAttributeRequest, UpdateMetaAttributeRequest,
} from '@/types/metadata';

const { Text } = Typography;

const DATA_TYPES = [
  { value: 'string', label: 'Рядок' },
  { value: 'integer', label: 'Ціле число' },
  { value: 'decimal', label: 'Десяткове' },
  { value: 'date', label: 'Дата' },
  { value: 'datetime', label: 'Дата+час' },
  { value: 'bool', label: 'Так/Ні' },
  { value: 'reference', label: 'Довідник' },
  { value: 'enum', label: 'Перерахування' },
];

interface Props {
  acqType: MetaDocumentTypeDto;
}

export default function AttributesEditor({ acqType }: Props) {
  const acqQc = useQueryClient();

  // Завжди фетчимо свіжу версію типу за кодом — щоб після CRUD атрибутів список оновлювався
  // навіть якщо батьківська сторінка передала застарілий snapshot з кешу списку.
  // Endpoint /catalog-types/{code} і /document-types/{code} обидва повертають тип з attributes.
  const acqIsCatalog = acqType.kind === 'catalog';
  const acqFreshQuery = useQuery({
    queryKey: ['metadata', acqIsCatalog ? 'catalog-types' : 'document-types', acqType.code],
    queryFn: () => acqIsCatalog
      ? metadataApi.getCatalogType(acqType.code)
      : metadataApi.getDocumentType(acqType.code),
    initialData: acqType,
  });
  const acqLiveType = acqFreshQuery.data ?? acqType;

  // Інвалідуємо як single-type cache (на якому ми сидимо), так і списки обох видів метатипів —
  // CatalogTypesPage / DocumentTypesPage отримають свіжі attributes при наступному відкритті модалки.
  const acqInvalidate = () => {
    acqQc.invalidateQueries({ queryKey: ['metadata', 'catalog-types'] });
    acqQc.invalidateQueries({ queryKey: ['metadata', 'document-types'] });
  };

  const acqAdd = useMutation({
    mutationFn: (req: CreateMetaAttributeRequest) => metadataApi.addAttribute(acqType.id, req),
    onSuccess: () => { acqInvalidate(); message.success('Реквізит додано'); },
    onError: (e: any) => message.error(e?.response?.data?.detail ?? e.message),
  });
  const acqUpdate = useMutation({
    mutationFn: ({ id, req }: { id: string; req: UpdateMetaAttributeRequest }) => metadataApi.updateAttribute(id, req),
    onSuccess: () => { acqInvalidate(); message.success('Реквізит оновлено'); },
    onError: (e: any) => message.error(e?.response?.data?.detail ?? e.message),
  });
  const acqDelete = useMutation({
    mutationFn: (id: string) => metadataApi.deleteAttribute(id),
    onSuccess: () => { acqInvalidate(); message.success('Реквізит видалено'); },
    onError: (e: any) => message.error(e?.response?.data?.detail ?? e.message),
  });

  const [acqModalOpen, setAcqModalOpen] = useState(false);
  const [acqEditing, setAcqEditing] = useState<MetaAttributeDto | null>(null);
  const [acqDraft, setAcqDraft] = useState<{
    code: string; name: string; dataType: string; referenceEntity?: string;
    isRequired: boolean; isIndexed: boolean; orderIdx: number; section: string;
  }>({ code: '', name: '', dataType: 'string', isRequired: false, isIndexed: false, orderIdx: 100, section: 'header' });

  const acqOpenAdd = () => {
    setAcqEditing(null);
    setAcqDraft({
      code: '', name: '', dataType: 'string',
      isRequired: false, isIndexed: false,
      orderIdx: (acqLiveType.attributes.length + 1) * 10, section: 'header',
    });
    setAcqModalOpen(true);
  };

  const acqOpenEdit = (acqRow: MetaAttributeDto) => {
    setAcqEditing(acqRow);
    setAcqDraft({
      code: acqRow.code, name: acqRow.name, dataType: acqRow.dataType,
      referenceEntity: acqRow.referenceEntity ?? undefined,
      isRequired: acqRow.isRequired, isIndexed: acqRow.isIndexed,
      orderIdx: acqRow.orderIdx, section: acqRow.section,
    });
    setAcqModalOpen(true);
  };

  const acqSubmit = async () => {
    if (!acqDraft.code || !acqDraft.name) { message.error('Code та Name обов\'язкові'); return; }
    if (acqEditing) {
      await acqUpdate.mutateAsync({
        id: acqEditing.id,
        req: {
          name: acqDraft.name,
          isRequired: acqDraft.isRequired,
          isIndexed: acqDraft.isIndexed,
          validationJson: null,
          orderIdx: acqDraft.orderIdx,
        },
      });
    } else {
      await acqAdd.mutateAsync({
        code: acqDraft.code, name: acqDraft.name, dataType: acqDraft.dataType as any,
        referenceEntity: acqDraft.dataType === 'reference' ? (acqDraft.referenceEntity ?? null) : null,
        isRequired: acqDraft.isRequired, isIndexed: acqDraft.isIndexed,
        validationJson: null, orderIdx: acqDraft.orderIdx,
      });
    }
    setAcqModalOpen(false);
  };

  // Групуємо за section для зручності перегляду
  const acqGrouped = acqLiveType.attributes.reduce<Record<string, MetaAttributeDto[]>>((m, a) => {
    (m[a.section] = m[a.section] ?? []).push(a);
    return m;
  }, {});

  const acqColumns = [
    {
      title: 'Code', dataIndex: 'code', width: 220,
      render: (v: string) => <Text code style={{ fontSize: 11 }}>{v}</Text>,
    },
    { title: 'Назва', dataIndex: 'name' },
    {
      title: 'Тип', dataIndex: 'dataType', width: 120,
      render: (v: string) => <Tag color={v === 'reference' ? 'purple' : v === 'enum' ? 'orange' : undefined}>{v}</Tag>,
    },
    {
      title: 'Reference', dataIndex: 'referenceEntity', width: 200,
      render: (v: string | null) => v ? <Text code style={{ fontSize: 11 }}>{v}</Text> : <Text type="secondary">—</Text>,
    },
    {
      title: 'Required', dataIndex: 'isRequired', width: 80,
      render: (v: boolean) => v ? '✓' : '',
    },
    {
      title: 'Order', dataIndex: 'orderIdx', width: 70,
    },
    {
      title: '', key: 'actions', width: 110,
      render: (_: unknown, acqRow: MetaAttributeDto) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => acqOpenEdit(acqRow)} />
          <Popconfirm title="Видалити реквізит?" onConfirm={() => acqDelete.mutate(acqRow.id)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Alert
        type="info" showIcon style={{ marginBottom: 12 }}
        message="CRUD реквізитів. BAS-Section ('header' або snake_case ТЧ). Для BAS-типів краще re-import з Phase 0 (upsert не зачіпає вручну додані реквізити)."
      />
      <Space style={{ marginBottom: 12 }}>
        <Button icon={<PlusOutlined />} onClick={acqOpenAdd}>Додати реквізит</Button>
        <Text type="secondary">Усього: {acqLiveType.attributes.length}</Text>
      </Space>

      {Object.entries(acqGrouped).sort(([a], [b]) => a === 'header' ? -1 : a.localeCompare(b)).map(([sec, acqAttrs]) => (
        <div key={sec} style={{ marginBottom: 12 }}>
          <Text strong>{sec === 'header' ? 'Шапка' : `Таблична частина: ${sec}`}</Text>
          <Table<MetaAttributeDto>
            rowKey="id"
            size="small"
            dataSource={acqAttrs.sort((a, b) => a.orderIdx - b.orderIdx)}
            columns={acqColumns}
            pagination={false}
            style={{ marginTop: 4 }}
          />
        </div>
      ))}

      <Modal
        open={acqModalOpen}
        title={acqEditing ? 'Редагувати реквізит' : 'Новий реквізит'}
        onCancel={() => setAcqModalOpen(false)}
        onOk={acqSubmit}
        okText="ОК"
        confirmLoading={acqAdd.isPending || acqUpdate.isPending}
        width={560}
      >
        <Form layout="vertical">
          <Form.Item label="Code (snake_case)" required>
            <Input
              value={acqDraft.code}
              onChange={(e) => setAcqDraft({ ...acqDraft, code: e.target.value })}
              disabled={!!acqEditing}
              placeholder="my_field"
            />
          </Form.Item>
          <Form.Item label="Назва" required>
            <Input
              value={acqDraft.name}
              onChange={(e) => setAcqDraft({ ...acqDraft, name: e.target.value })}
            />
          </Form.Item>
          <Form.Item label="Тип даних" required>
            <Select
              value={acqDraft.dataType}
              onChange={(v) => setAcqDraft({ ...acqDraft, dataType: v })}
              options={DATA_TYPES}
              disabled={!!acqEditing}
            />
          </Form.Item>
          {acqDraft.dataType === 'reference' && (
            <Form.Item label="Довідник (referenceEntity)">
              <Select
                value={acqDraft.referenceEntity}
                onChange={(v) => setAcqDraft({ ...acqDraft, referenceEntity: v })}
                options={acqListReferenceTypes().map((r) => ({ value: capitalizeFirst(r.value), label: r.label }))}
                disabled={!!acqEditing}
                showSearch
                filterOption={(input, opt) => String(opt?.label ?? '').toLowerCase().includes(input.toLowerCase())}
              />
            </Form.Item>
          )}
          <Form.Item label="OrderIdx (порядок)">
            <InputNumber
              value={acqDraft.orderIdx}
              onChange={(v) => setAcqDraft({ ...acqDraft, orderIdx: v ?? 0 })}
              style={{ width: '100%' }}
            />
          </Form.Item>
          <Space>
            <span>Required:</span>
            <Switch checked={acqDraft.isRequired} onChange={(v) => setAcqDraft({ ...acqDraft, isRequired: v })} />
            <span style={{ marginLeft: 16 }}>Indexed:</span>
            <Switch checked={acqDraft.isIndexed} onChange={(v) => setAcqDraft({ ...acqDraft, isIndexed: v })} />
          </Space>
        </Form>
      </Modal>
    </div>
  );
}

function capitalizeFirst(s: string) {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
